import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";

import { GCP } from "./types";
import { useGeoReferencer } from "./useGeoReferencer";

const GCP_RADIUS = 8;
const GCP_FONT_SIZE = 11;
const MIN_SCALE = 0.05;
const MAX_SCALE = 20;
const FIT_PADDING = 20;

interface ViewportState {
  offsetX: number;
  offsetY: number;
  scale: number;
}

/**
 * Imperative render function -- draws the image and GCP markers on the canvas.
 * Kept outside the component to avoid stale closure issues.
 */
function renderCanvas(
  canvas: HTMLCanvasElement,
  img: HTMLImageElement | null,
  viewport: ViewportState,
  canvasWidth: number,
  canvasHeight: number,
  gcps: GCP[],
  activeGcpId: string | null
): void {
  const ctx = canvas.getContext("2d");
  if (!ctx || canvasWidth === 0 || canvasHeight === 0) return;

  const dpr = window.devicePixelRatio || 1;
  canvas.width = canvasWidth * dpr;
  canvas.height = canvasHeight * dpr;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, canvasWidth, canvasHeight);

  const { offsetX, offsetY, scale } = viewport;

  // Draw image
  if (img) {
    ctx.save();
    ctx.translate(offsetX, offsetY);
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0);
    ctx.restore();
  }

  // Draw GCP markers
  for (const gcp of gcps) {
    // Skip unset image points (default 0,0 with no map point means not yet placed)
    if (gcp.imageX === 0 && gcp.imageY === 0 && gcp.mapLng === null) continue;
    const canvasX = gcp.imageX * scale + offsetX;
    const canvasY = gcp.imageY * scale + offsetY;

    const isActive = gcp.id === activeGcpId;
    const isComplete = gcp.mapLng != null && gcp.mapLat != null;

    // Outer circle
    ctx.beginPath();
    ctx.arc(canvasX, canvasY, GCP_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = isActive ? "rgba(255, 193, 7, 0.8)" : isComplete ? "rgba(76, 175, 80, 0.8)" : "rgba(244, 67, 54, 0.8)";
    ctx.fill();
    ctx.strokeStyle = "white";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Crosshair
    const crossSize = GCP_RADIUS + 4;
    ctx.beginPath();
    ctx.moveTo(canvasX - crossSize, canvasY);
    ctx.lineTo(canvasX + crossSize, canvasY);
    ctx.moveTo(canvasX, canvasY - crossSize);
    ctx.lineTo(canvasX, canvasY + crossSize);
    ctx.strokeStyle = isActive ? "rgba(255, 193, 7, 0.9)" : "rgba(255, 255, 255, 0.7)";
    ctx.lineWidth = 1;
    ctx.stroke();

    // Label number
    const labelNum = gcp.label.replace("GCP ", "");
    ctx.font = `bold ${GCP_FONT_SIZE}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "white";
    ctx.fillText(labelNum, canvasX, canvasY);
  }
}

const ImageViewer: React.FC = () => {
  const { imageUrl, imageWidth, imageHeight, gcps, activeGcpId, mode, setImagePoint } = useGeoReferencer();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const viewportRef = useRef<ViewportState>({ offsetX: 0, offsetY: 0, scale: 1 });
  const isDragging = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  const animFrameRef = useRef<number>(0);
  const canvasSizeRef = useRef({ width: 0, height: 0 });
  const gcpsRef = useRef(gcps);
  const activeGcpIdRef = useRef(activeGcpId);
  const hasFittedRef = useRef(false);

  // Keep refs in sync
  gcpsRef.current = gcps;
  activeGcpIdRef.current = activeGcpId;

  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);

  // Update ref when state changes
  canvasSizeRef.current = canvasSize;

  // Schedule a canvas render using the latest ref values (no stale closures)
  const scheduleRender = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
    animFrameRef.current = requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      renderCanvas(
        canvas,
        imageRef.current,
        viewportRef.current,
        canvasSizeRef.current.width,
        canvasSizeRef.current.height,
        gcpsRef.current,
        activeGcpIdRef.current
      );
    });
  }, []);

  // Load the image element
  useEffect(() => {
    if (!imageUrl) {
      imageRef.current = null;
      setImageLoaded(false);
      hasFittedRef.current = false;
      return;
    }

    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // Observe container size
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setCanvasSize({ width: Math.floor(width), height: Math.floor(height) });
        }
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Fit image to canvas when first loaded and canvas has a size
  useEffect(() => {
    if (!imageLoaded || !imageWidth || !imageHeight || !canvasSize.width || !canvasSize.height || hasFittedRef.current) return;

    const scaleX = (canvasSize.width - FIT_PADDING * 2) / imageWidth;
    const scaleY = (canvasSize.height - FIT_PADDING * 2) / imageHeight;
    const scale = Math.min(scaleX, scaleY);
    const offsetX = (canvasSize.width - imageWidth * scale) / 2;
    const offsetY = (canvasSize.height - imageHeight * scale) / 2;
    viewportRef.current = { offsetX, offsetY, scale };
    hasFittedRef.current = true;
    scheduleRender();
  }, [imageLoaded, imageWidth, imageHeight, canvasSize.width, canvasSize.height, scheduleRender]);

  // Re-render when reactive state changes (gcps, activeGcpId, canvasSize)
  useEffect(() => {
    scheduleRender();
  }, [gcps, activeGcpId, canvasSize, imageLoaded, scheduleRender]);

  const canvasToImage = useCallback((canvasX: number, canvasY: number): [number, number] => {
    const { offsetX, offsetY, scale } = viewportRef.current;
    return [(canvasX - offsetX) / scale, (canvasY - offsetY) / scale];
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (e.button !== 0) return;

      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const canvasX = e.clientX - rect.left;
      const canvasY = e.clientY - rect.top;

      if (mode === "place-image" && activeGcpId) {
        const [imgX, imgY] = canvasToImage(canvasX, canvasY);
        if (imgX >= 0 && imgX <= imageWidth && imgY >= 0 && imgY <= imageHeight) {
          setImagePoint(activeGcpId, imgX, imgY);
        }
        return;
      }

      isDragging.current = true;
      lastMouse.current = { x: e.clientX, y: e.clientY };
    },
    [mode, activeGcpId, canvasToImage, imageWidth, imageHeight, setImagePoint]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isDragging.current) return;

      const dx = e.clientX - lastMouse.current.x;
      const dy = e.clientY - lastMouse.current.y;
      lastMouse.current = { x: e.clientX, y: e.clientY };

      viewportRef.current.offsetX += dx;
      viewportRef.current.offsetY += dy;
      scheduleRender();
    },
    [scheduleRender]
  );

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // Attach wheel listener imperatively with { passive: false } so preventDefault() works.
  // React's onWheel registers as passive by default, which blocks preventDefault().
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();

      const rect = canvas.getBoundingClientRect();

      const cursorX = e.clientX - rect.left;
      const cursorY = e.clientY - rect.top;

      const zoomFactor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      const { offsetX, offsetY, scale } = viewportRef.current;
      const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale * zoomFactor));

      viewportRef.current = {
        offsetX: cursorX - (cursorX - offsetX) * (newScale / scale),
        offsetY: cursorY - (cursorY - offsetY) * (newScale / scale),
        scale: newScale,
      };
      scheduleRender();
    };

    canvas.addEventListener("wheel", handleWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", handleWheel);
  }, [scheduleRender]);

  if (!imageUrl) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%" p={2}>
        <Typography color="text.secondary">No image loaded</Typography>
      </Box>
    );
  }

  const isPlacing = mode === "place-image";

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        backgroundImage: `
        linear-gradient(45deg, #eee 25%, transparent 25%), 
        linear-gradient(-45deg, #eee 25%, transparent 25%), 
        linear-gradient(45deg, transparent 75%, #eee 75%), 
        linear-gradient(-45deg, transparent 75%, #eee 75%)
      `,
        backgroundSize: "20px 20px",
        backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
        backgroundColor: "#fff",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: canvasSize.width || "100%",
          height: canvasSize.height || "100%",
          cursor: isPlacing ? "crosshair" : "grab",
          display: "block",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />
      {isPlacing && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: "50%",
            transform: "translateX(-50%)",
            bgcolor: "rgba(0,0,0,0.7)",
            color: "white",
            px: 2,
            py: 0.5,
            borderRadius: 1,
            fontSize: "0.875rem",
            pointerEvents: "none",
          }}
        >
          Click on the image to place point
        </Box>
      )}
    </Box>
  );
};

export default ImageViewer;
